import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import SiteFooter from '../components/SiteFooter';
import { toast } from 'sonner';
import ThemeToggle from '../components/ThemeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OnboardingPage = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    voice_intensity: 50,
    stoic_level: 50,
    directness: 50
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('sage_token');
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('sage_token');
      await axios.put(
        `${API}/profile`,
        profile,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Profile configured successfully');
      setTimeout(() => navigate('/dashboard'), 1000);
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian">
      <div className="overlay-noise" />
      <div className="fixed top-5 right-5 z-30">
        <ThemeToggle size="sm" className="border border-white/14 bg-stone-950/40 backdrop-blur-sm rounded-sm" />
      </div>
      
      <div className="relative z-10 max-w-2xl mx-auto px-6 py-12">
        <div className="mb-12">
          <h1 
            className="text-5xl md:text-6xl font-light tracking-tight text-zinc-100 mb-4"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Configure SAGE
          </h1>
          <p className="text-sm font-mono uppercase tracking-widest text-zinc-400">
            Shape Your Oracle's Persona
          </p>
        </div>

        <div className="space-y-12">
          {/* Voice Intensity */}
          <div className="bg-stone-950/50 border border-white/5 backdrop-blur-sm rounded-sm p-8">
            <div className="mb-6">
              <h3 className="text-2xl font-light text-white mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Voice Intensity
              </h3>
              <p className="text-sm text-zinc-500">How assertive should SAGE be in guidance?</p>
            </div>
            <div className="space-y-4">
              <Slider
                value={[profile.voice_intensity]}
                onValueChange={([value]) => setProfile({...profile, voice_intensity: value})}
                max={100}
                step={1}
                className="w-full"
                data-testid="voice-intensity-slider"
              />
              <div className="flex justify-between text-xs font-mono text-zinc-400">
                <span>Gentle</span>
                <span className="text-white">{profile.voice_intensity}</span>
                <span>Commanding</span>
              </div>
            </div>
          </div>

          {/* Stoic Level */}
          <div className="bg-stone-950/50 border border-white/5 backdrop-blur-sm rounded-sm p-8">
            <div className="mb-6">
              <h3 className="text-2xl font-light text-white mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Stoic Level
              </h3>
              <p className="text-sm text-zinc-500">Balance between philosophy and practicality</p>
            </div>
            <div className="space-y-4">
              <Slider
                value={[profile.stoic_level]}
                onValueChange={([value]) => setProfile({...profile, stoic_level: value})}
                max={100}
                step={1}
                className="w-full"
                data-testid="stoic-level-slider"
              />
              <div className="flex justify-between text-xs font-mono text-zinc-400">
                <span>Practical</span>
                <span className="text-white">{profile.stoic_level}</span>
                <span>Philosophical</span>
              </div>
            </div>
          </div>

          {/* Directness */}
          <div className="bg-stone-950/50 border border-white/5 backdrop-blur-sm rounded-sm p-8">
            <div className="mb-6">
              <h3 className="text-2xl font-light text-white mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Directness
              </h3>
              <p className="text-sm text-zinc-500">How straightforward should SAGE communicate?</p>
            </div>
            <div className="space-y-4">
              <Slider
                value={[profile.directness]}
                onValueChange={([value]) => setProfile({...profile, directness: value})}
                max={100}
                step={1}
                className="w-full"
                data-testid="directness-slider"
              />
              <div className="flex justify-between text-xs font-mono text-zinc-400">
                <span>Subtle</span>
                <span className="text-white">{profile.directness}</span>
                <span>Blunt</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 flex gap-4">
          <Button
            onClick={() => navigate('/dashboard')}
            className="flex-1 bg-transparent border border-white/20 text-white hover:bg-white/5 rounded-sm px-8 py-6 transition-all"
          >
            Skip for Now
          </Button>
          <button
            type="button"
            onClick={handleSubmit}
            className="flex-1 threshold-button rounded-sm px-6 py-4 text-base md:text-lg"
            disabled={loading}
            data-testid="save-profile-button"
            aria-label="Continue"
          >
            <span>{loading ? 'Saving…' : 'Continue'}</span>
            {!loading && (
              <span className="threshold-button-arrow" aria-hidden="true">→</span>
            )}
          </button>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
};

export default OnboardingPage;